import {
  Box,
  Grid,
  ImageList,
  ImageListItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState, type FC, useEffect, useRef } from "react";
import { MagicShortcutModal } from "../modals/MagicShortcutModal";
import {
  CLOTHE_SEGMENT_TYPE_ENUM,
  MAGIC_SHORTCUT_PARAM_TYPE_ENUM,
  MAGIC_SHORTCUT_TYPE_ENUM,
  MAGIC_TYPE_ENUM,
  SEGMENT_TYPE_ENUM,
} from "../../utils/constants";
import styled from "styled-components";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";

type MainMagicShortcutParamsType = {
  magic_type: MAGIC_TYPE_ENUM;
  init_image: MAGIC_SHORTCUT_PARAM_TYPE_ENUM;
  segment_prompt?: SEGMENT_TYPE_ENUM | CLOTHE_SEGMENT_TYPE_ENUM | undefined;
  refer_image?: string | undefined;
  prompt: string;
};

export type MainMagicShortcutType = {
  type: MAGIC_SHORTCUT_TYPE_ENUM;
  magic: string;
  teach: string;
  params: MainMagicShortcutParamsType;
};

// ---------------------------------------------------------------------------------------------
type ShortcutsProps = {
  list: MainMagicShortcutType[];
  onMagicShortcutStartClick: () => void;
  onMagicShortcutModalOpenChange: (
    shortcut: MainMagicShortcutType | null
  ) => void;
};

export const Shortcuts: FC<ShortcutsProps> = (props) => {
  // responsive UI
  const theme = useTheme();
  const notPhone = useMediaQuery(theme.breakpoints.up("sm"));

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const imgListRef = useRef<HTMLUListElement | null>(null);

  const [scrolling, setScrolling] = useState(true);
  const [magicShortcut, setMagicShortcut] =
    useState<MainMagicShortcutType | null>(null);
  const [showMagicShortcutModal, setShowMagicShortcutModal] = useState(false);

  useEffect(() => {
    let timerId: string | number | NodeJS.Timer | null | undefined;

    if (scrolling) {
      timerId = setInterval(() => {
        if (imgListRef.current && wrapperRef.current) {
          const scrollBottom = imgListRef.current.scrollHeight / 2;
          if (imgListRef.current.scrollTop >= scrollBottom) {
            imgListRef.current.scrollTop = 0;
          } else {
            imgListRef.current.scrollTop = scrollBottom;
          }
        }
      }, 5000);
    }

    return () => {
      if (timerId) {
        clearInterval(timerId);
      }
    };
  }, [scrolling]);

  const handleImgClick = (shortcut: MainMagicShortcutType) => {
    setMagicShortcut(shortcut);
    props.onMagicShortcutModalOpenChange(shortcut);
    setShowMagicShortcutModal(true);
  };

  const handleImgRowHover = (flag: boolean) => setScrolling(flag);

  return (
    <Grid size={{ xs: 12, sm: 8 }}>
      {/* 快捷功能列表 */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "5px",
          padding: "0 16px 16px 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <TipsAndUpdatesIcon
            color="secondary"
            sx={{ width: "18px", height: "18px" }}
          />
          <Typography
            variant="h5"
            color="secondary"
            fontStyle="italic"
            fontWeight={700}
          >
            点击图片，开始快捷魔法之旅：
          </Typography>
        </Box>

        <Box ref={wrapperRef}>
          <ImageList
            sx={{
              width: notPhone ? 700 : "100%",
              height: notPhone ? 180 : 108,
              overflowY: "hidden",
            }}
            cols={3}
            rowHeight={notPhone ? 180 : 108}
            ref={imgListRef}
          >
            {props.list.map((shortcut, idx) => (
              <ImageListItem
                key={`魔法快捷功能-${idx}`}
                onMouseOver={() => handleImgRowHover(false)}
                onMouseLeave={() => handleImgRowHover(true)}
              >
                <StyledImgPC
                  srcSet={`${shortcut.magic}?w=180&h=180&fit=crop&auto=format&dpr=2 2x`}
                  src={`${shortcut.magic}?w=180&h=180&fit=crop&auto=format`}
                  alt={`魔法快捷功能-${idx}`}
                  loading="lazy"
                  style={{
                    border: `1px solid ${theme.palette.secondary.dark}`,
                  }}
                  onClick={() => handleImgClick(shortcut)}
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Box>

        {magicShortcut && (
          <MagicShortcutModal
            open={showMagicShortcutModal}
            shortcut={magicShortcut}
            onStartClick={() => {
              setShowMagicShortcutModal(false);
              props.onMagicShortcutStartClick();
            }}
            onModalClose={() => {
              setShowMagicShortcutModal(false);
              props.onMagicShortcutModalOpenChange(null);
            }}
          />
        )}
      </Box>
    </Grid>
  );
};

const StyledImgPC = styled.img`
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0.19);
  border-radius: 5px;
  &:hover {
    cursor: pointer;
  }
`;
